import { Component, HostBinding, OnDestroy, Inject, inject, InjectionToken, NgZone } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { AuthService } from '@cleverly/shared/api-authorization';
import { EnvironmentService } from '@cleverly/shared/data';
import { EnvironmentService as CoreEnvironmentService } from '@cleverly/shared/core';
import { HubSpotConversationService } from '@cleverly/shared/data/services/hubspotConversation.service';
import { PushmanagerService } from '@cleverly/shared/mobile-device/services/pushmanager.service';
import { Subject, take, Observable, takeUntil, filter, switchMap } from 'rxjs';
import { AppLoadingService } from './services/app-loading.service';
import { StatusBarService } from './services/status-bar.service';
import { AppInsightsService, AnalyticsService, ConsentService, MatomoService } from '@cleverly/shared/common';
import { DeepLinkService } from './services/deep-link.service';
import { ChunkLoadErrorService } from './services/chunk-load-error.service';
import { AppDiagnosticsService } from './services/app-diagnostics.service';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'portal-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    host: { class: 'flex flex-column h-full' },
    standalone: false
})
export class AppComponent implements OnDestroy {
    @HostBinding('class') get getHostClasses() {
        const userAgent = navigator.userAgent || navigator.vendor;
        if (/android/i.test(userAgent) || /iPad|iPhone|iPod/.test(userAgent) || navigator.platform.toUpperCase().indexOf('MAC') >= 0) {
            return '';
        }
        return 'nice-scrollbars';
    }

    title = 'cleverly-angular-clients';
    private unsubscribeReceiver: Subject<void> = new Subject();
    
    // Add loading observable
    loading$: Observable<boolean>;
    private gtmId = inject('googleTagManagerId' as any, { optional: true }) as string | null;
    private gtmAuth = inject('googleTagManagerAuth' as any, { optional: true }) as string | null;
    private gtmPreview = inject('googleTagManagerPreview' as any, { optional: true }) as string | null;
    private ngZone = inject(NgZone);
    private router = inject(Router);
    private authService = inject(AuthService);
    private pushManagerService = inject(PushmanagerService);
    private appLoadingService = inject(AppLoadingService);
    private environmentService = inject(EnvironmentService);
    private appInsightsService = inject(AppInsightsService);
    private analyticsService = inject(AnalyticsService);
    private consentService = inject(ConsentService);
    private deepLinkService = inject(DeepLinkService);
    private chunkLoadErrorService = inject(ChunkLoadErrorService);
    private statusBarService = inject(StatusBarService);
    // private diagnosticsService = inject(AppDiagnosticsService);
    private coreEnvironmentService = inject(CoreEnvironmentService);
    private matomoService = inject(MatomoService);
    
    constructor() {
        // Initialize loading observable
        this.loading$ = this.appLoadingService.loading$;        
        
        // Initialize consent and analytics outside Angular zone to prevent
        // change detection cycles from Capacitor plugin calls on iOS
        this.ngZone.runOutsideAngular(() => {
            // Initialize consent management
            this.initializeConsent();
            
            // Initialize Universal Analytics (GTM for web, Firebase for native)
            this.initializeAnalytics();

            // Initialize Matomo Analytics
            this.initializeMatomo();
        });
        
        // Hide the initial loading screen from index.html
        this.hideInitialLoadingScreen();
        
        // Initialize deep link handling
        this.deepLinkService.initialize();
        
        // Setup chunk loading error handler
        this.chunkLoadErrorService.initialize();

        // Setup automatic screen view tracking for native apps
        if (Capacitor.isNativePlatform()) {
            this.setupNativeScreenTracking();
        }

        // Setup Application Insights only when user is authenticated.
        // Run outside Angular Zone — App Insights SDK starts internal setInterval
        // timers (telemetry batching) that keep Zone permanently unstable and
        // block router navigation on iOS.
        this.ngZone.runOutsideAngular(() => {
            this.authService.getUser$()
                .pipe(
                    takeUntil(this.unsubscribeReceiver),
                    filter(user => !!user), // Only proceed when user is authenticated
                    switchMap(() => this.environmentService.getEnvironment()),
                    take(1)
                )
                .subscribe(env => {
                    if (env?.applicationInsightsInstrumentationKey?.length > 0) {
                        // Determine platform-specific cloud role
                        const platform = Capacitor.getPlatform();
                        let cloudRole = 'Portal-Frontend-Web';
                        
                        if (platform === 'android') {
                            cloudRole = 'Portal-Frontend-Android';
                        } else if (platform === 'ios') {
                            cloudRole = 'Portal-Frontend-iOS';
                        }
                        
                        this.appInsightsService.initialize({
                            instrumentationKey: env.applicationInsightsInstrumentationKey,
                            router: this.router,
                            authService: this.authService,
                            cloudRole: cloudRole,
                            appVersion: env.appVersion || '1.0.0'
                        });
                    }   
                });
        });

        // Initialize push service after app initialization
        this.pushManagerService.initPushservice();
    }
    
    /**
     * Initialize consent management
     * Only needed for native apps - web uses Cookiebot via GTM
     */
    private async initializeConsent(): Promise<void> {
        // Only initialize for native apps - web consent is handled by Cookiebot
        if (!Capacitor.isNativePlatform()) {
            console.log('Web platform: Consent managed by Cookiebot via GTM');
            return;
        }

        try {
            await this.consentService.initialize();
            console.log('✅ Consent service initialized for native app');
        } catch (error) {
            console.error('❌ Failed to initialize consent service:', error);
        }
    }
    
    /**
     * Initialize Universal Analytics Service
     * Automatically uses GTM for web and Firebase Analytics for native apps
     */
    private async initializeAnalytics(): Promise<void> {
        if (!this.gtmId) {
            console.warn('AppComponent: GTM ID is null/undefined - Analytics will not be initialized');
            return;
        }

        try {
            await this.analyticsService.initialize({
                gtm: {
                    id: this.gtmId,
                    auth: this.gtmAuth || undefined,
                    preview: this.gtmPreview || undefined
                },
                firebase: {
                    enabled: true // Enable Firebase Analytics for native apps
                }
            });

            const platform = this.analyticsService.getPlatformName();
            console.log(`✅ Analytics initialized successfully for ${platform}`);

            // Track app launch
            await this.analyticsService.trackEvent('app_launch', {
                platform: Capacitor.getPlatform(),
                is_native: Capacitor.isNativePlatform(),
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            console.error('❌ Failed to initialize analytics:', error);
        }
    }

    /**
     * Setup automatic screen view tracking for native apps
     * Web tracking is handled automatically by GTM
     * 
     * IMPORTANT: Analytics calls run outside Angular zone to prevent
     * change detection cycles that cause slow navigation on iOS
     */
    private setupNativeScreenTracking(): void {
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.unsubscribeReceiver)
            )
            .subscribe((event: NavigationEnd) => {
                // Run analytics tracking outside Angular zone to prevent
                // change detection from blocking the main thread
                this.ngZone.runOutsideAngular(async () => {
                    try {
                        const screenName = this.getScreenNameFromUrl(event.urlAfterRedirects);
                        await this.analyticsService.trackScreenView(screenName);
                        console.log(`📱 Screen view tracked: ${screenName}`);
                    } catch (error) {
                        console.error('Failed to track screen view:', error);
                    }
                });
            });
    }

    /**
     * Convert URL to readable screen name for analytics
     */
    private getScreenNameFromUrl(url: string): string {
        // Remove query parameters and fragments
        const cleanUrl = url.split('?')[0].split('#')[0];
        
        // Remove leading slash and convert to title case
        const parts = cleanUrl.slice(1).split('/');
        return parts
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' / ') || 'Home';
    }

    private hideInitialLoadingScreen(): void {
        // Remove the initial loading screen from index.html
        const initialLoading = document.getElementById('initial-loading');
        if (initialLoading) {
            initialLoading.style.opacity = '0';
            setTimeout(() => {
                initialLoading.remove();
            }, 300);
        }
    }
    
    /**
     * Initialize Matomo Analytics
     * Determines platform and initializes with correct Custom Dimension.
     * Wires setUserId and visit-scoped dimensions after authentication.
     */
    private initializeMatomo(): void {
        const env = this.coreEnvironmentService.environment;
        if (!env.matomoUrl || !env.matomoSiteId) {
            return;
        }

        // Determine platform for Custom Dimension 1
        const capacitorPlatform = Capacitor.getPlatform();
        let platform = 'WebApp';
        if (capacitorPlatform === 'ios' || capacitorPlatform === 'android') {
            platform = 'NativeApp';
        }

        this.matomoService.initialize({
            siteId: env.matomoSiteId,
            trackerUrl: env.matomoUrl,
            platform: platform
        });

        // On web: bridge Cookiebot consent → Matomo cookie consent
        if (!Capacitor.isNativePlatform()) {
            this.matomoService.listenForCookiebotConsent();
        } else {
            // On native: bridge ConsentService → Matomo cookie consent
            this.consentService.getStoredConsent().then(consent => {
                if (consent?.analytics) {
                    this.matomoService.grantCookieConsent();
                }
            });
        }

        // After login: set UserId + visit-scoped dimensions
        // On logout (user becomes null): reset UserId
        this.authService.getUser$()
            .pipe(
                takeUntil(this.unsubscribeReceiver)
            )
            .subscribe(user => {
                if (user) {
                    const personId = this.authService.currentPersonId.value;
                    if (personId) {
                        this.matomoService.setUserId(personId);
                    }

                    const accountGroupId = this.authService.currentAccountGroupId.value;
                    if (accountGroupId) {
                        this.matomoService.setAccountGroupId(accountGroupId);
                    }

                    // Set UserRole from JWT claims (primary role for analytics)
                    const roles = this.authService.getRoles();
                    if (roles?.length) {
                        // Admin-only users are excluded from analytics
                        const isAdminOnly = roles.includes('Admin') && !roles.includes('Parent') && !roles.includes('Student') && !roles.includes('Pedagogue');
                        if (isAdminOnly) {
                            this.matomoService.optUserOut();
                            return;
                        }
                        // Pick primary role: Pedagogue > AccountOwner > Parent > Student
                        const primaryRole = this.determinePrimaryRole(roles);
                        this.matomoService.setUserRole(primaryRole);
                    }
                } else {
                    this.matomoService.resetUserId();
                }
            });
    }

    /**
     * Determine the primary role for analytics.
     * Priority: Pedagogue > AccountOwner > Parent > Student
     */
    private determinePrimaryRole(roles: string[]): string {
        if (roles.includes('Pedagogue')) return 'Pedagogue';
        if (roles.includes('AccountOwner')) return 'AccountOwner';
        if (roles.includes('Parent')) return 'Parent';
        if (roles.includes('Student')) return 'Student';
        return roles[0] || 'Unknown';
    }

    ngOnDestroy(): void {
        this.unsubscribeReceiver.next();
        this.unsubscribeReceiver.complete();
        this.matomoService.destroy();
    }
}